/** Colour helpers used to derive a MUI palette from the tenant's brand colour. */

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

export const isValidHexColor = (value: unknown): value is string =>
  typeof value === "string" && HEX_RE.test(value.trim());

/**
 * Normalises `#abc`, `#abcd`, `#rrggbb` and `#rrggbbaa` down to a plain
 * 6-digit `#rrggbb` string. The API stores brand colours with an alpha
 * suffix (e.g. `#072541FF`) which several CSS colour functions choke on.
 */
export const normalizeHexColor = (
  value: unknown,
  fallback = "#000000",
): string => {
  if (!isValidHexColor(value)) return fallback;
  let hex = value.trim().replace("#", "");
  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .slice(0, 3)
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return `#${hex.slice(0, 6).toUpperCase()}`;
};

const toRgb = (hex: string): Rgb => {
  const value = normalizeHexColor(hex).replace("#", "");
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
};

const toHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b]
    .map((channel) =>
      Math.max(0, Math.min(255, Math.round(channel)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")
    .toUpperCase()}`;

/** Mixes the colour towards white by `percent` (0-100). */
export const getLightColorShade = (hex: string, percent = 20): string => {
  const { r, g, b } = toRgb(hex);
  const ratio = percent / 100;
  return toHex({
    r: r + (255 - r) * ratio,
    g: g + (255 - g) * ratio,
    b: b + (255 - b) * ratio,
  });
};

/** Mixes the colour towards black by `percent` (0-100). */
export const getDarkColorShade = (hex: string, percent = 20): string => {
  const { r, g, b } = toRgb(hex);
  const ratio = 1 - percent / 100;
  return toHex({ r: r * ratio, g: g * ratio, b: b * ratio });
};

/** Returns black or white, whichever reads better on top of `hex`. */
export const getContrastColor = (hex: string): string => {
  const { r, g, b } = toRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#000000" : "#FFFFFF";
};

/* -------------------------------- contrast -------------------------------- */

/** WCAG relative luminance of a colour. */
const relativeLuminance = (hex: string): number => {
  const { r, g, b } = toRgb(hex);
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** WCAG contrast ratio between two colours, from 1:1 to 21:1. */
export const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Nudges `color` towards white or black — whichever direction increases
 * contrast against `background` — until it clears `target`, preserving hue as
 * far as possible.
 *
 * This exists because the tenant's brand colour is applied to both the light
 * and dark palettes. A dark brand colour (this tenant's is `#072541`) sits at
 * 1.14:1 on the dark surface, which makes every price and card title that uses
 * `primary.main` unreadable. A fixed ±20% shade cannot fix that; the shift has
 * to be driven by the measured ratio.
 */
export const ensureContrast = (
  color: string,
  background: string,
  target = 4.5,
): string => {
  if (contrastRatio(color, background) >= target) return color;

  // Move away from the background's own luminance.
  const towardsWhite = relativeLuminance(background) < 0.5;

  for (let percent = 5; percent <= 100; percent += 5) {
    const candidate = towardsWhite
      ? getLightColorShade(color, percent)
      : getDarkColorShade(color, percent);
    if (contrastRatio(candidate, background) >= target) return candidate;
  }

  // Nothing in between worked, so fall back to the extreme.
  return towardsWhite ? "#FFFFFF" : "#000000";
};
